export type PortalDeliveryStatus = 'development' | 'validation' | 'approved' | 'planned';

export type PortalDeliveryStageTone = 'blue' | 'purple' | 'red' | 'amber' | 'green' | 'sky' | 'orange' | 'slate';

export type PortalDeliveryChecklistItem = {
  id: string;
  label: string;
  done: boolean;
  dueLabel?: string;
};

export type PortalDeliveryComment = {
  id: string;
  authorName: string;
  body: string;
  whenLabel: string;
};

export type PortalDeliveryFile = {
  id: string;
  name: string;
  sizeLabel: string;
};

export type PortalDeliveryHistoryEntry = {
  id: string;
  title: string;
  detail: string;
  whenLabel: string;
};

export type PortalDeliveryItem = {
  id: string;
  title: string;
  version: string;
  description: string;
  detailDescription: string;
  stageLabel: string;
  stageTone: PortalDeliveryStageTone;
  ownerName: string;
  ownerRole: string;
  dueLabel: string;
  dueLongLabel: string;
  status: PortalDeliveryStatus;
  priorityLabel: string;
  progressPercent: number;
  waitingClient: boolean;
  checklist: PortalDeliveryChecklistItem[];
  files: PortalDeliveryFile[];
  comments: PortalDeliveryComment[];
  history: PortalDeliveryHistoryEntry[];
};

export type PortalDeliveriesData = {
  projectId: string;
  progressPercent: number;
  items: PortalDeliveryItem[];
};

export const portalDeliveriesByProjectId: Record<string, PortalDeliveriesData> = {
  crm: {
    projectId: 'crm',
    progressPercent: 60,
    items: [
      {
        id: 'del-requirements',
        title: 'Análise de requisitos',
        version: 'v1.2',
        description: 'Documento de requisitos e escopo alinhado com o time comercial.',
        detailDescription:
          'Consolidação dos requisitos funcionais e não funcionais do CRM, com validação das áreas comercial e de operações.',
        stageLabel: 'Planejamento',
        stageTone: 'blue',
        ownerName: 'Mariana Silva',
        ownerRole: 'Gerente do projeto',
        dueLabel: '15/09/2026',
        dueLongLabel: '15 de set. de 2026',
        status: 'approved',
        priorityLabel: 'Alta',
        progressPercent: 100,
        waitingClient: false,
        checklist: [
          { id: 'c1', label: 'Workshop de descoberta', done: true },
          { id: 'c2', label: 'Documento revisado', done: true },
          { id: 'c3', label: 'Aceite formal', done: true },
        ],
        files: [{ id: 'f1', name: 'Requisitos_CRM_v1.2.pdf', sizeLabel: '540 KB' }],
        comments: [
          {
            id: 'cm1',
            authorName: 'Mariana Silva',
            body: 'Versão aprovada pelo cliente em 15/09.',
            whenLabel: '15/09',
          },
        ],
        history: [
          {
            id: 'h1',
            title: 'Entrega aprovada',
            detail: 'Aceite registrado por Ana Silva',
            whenLabel: '15/09/2026',
          },
        ],
      },
      {
        id: 'del-test-env',
        title: 'Ambiente de testes configurado',
        version: 'v1.0',
        description: 'Ambiente de homologação do CRM disponível para validação.',
        detailDescription:
          'Ambiente de testes do CRM provisionado com usuários do cliente, integrações iniciais e dados de exemplo para validação funcional.',
        stageLabel: 'Configuração',
        stageTone: 'purple',
        ownerName: 'Rafael Oliveira',
        ownerRole: 'Desenvolvedor',
        dueLabel: '30/10/2026',
        dueLongLabel: '30 de out. de 2026',
        status: 'validation',
        priorityLabel: 'Alta',
        progressPercent: 80,
        waitingClient: true,
        checklist: [
          { id: 'c1', label: 'Acesso ao ambiente disponibilizado', done: true },
          { id: 'c2', label: 'Usuários do cliente cadastrados', done: true },
          { id: 'c3', label: 'Integração com ERP conectada', done: true },
          { id: 'c4', label: 'Validação do cliente', done: false, dueLabel: '30/10/2026' },
        ],
        files: [
          { id: 'f1', name: 'Guia_acesso_homologacao.pdf', sizeLabel: '220 KB' },
          { id: 'f2', name: 'Credenciais_ambiente.xlsx', sizeLabel: '48 KB' },
          { id: 'f3', name: 'Checklist_validacao.pdf', sizeLabel: '180 KB' },
        ],
        comments: [
          {
            id: 'cm1',
            authorName: 'Rafael Oliveira',
            body: 'Ambiente liberado. Aguardando validação do cliente.',
            whenLabel: 'há 3 horas',
          },
          {
            id: 'cm2',
            authorName: 'Mariana Silva',
            body: 'Favor confirmar o acesso dos usuários da comercial.',
            whenLabel: 'há 5 horas',
          },
          {
            id: 'cm3',
            authorName: 'Ana Souza',
            body: 'Incluí o roteiro de testes no checklist.',
            whenLabel: 'ontem',
          },
          {
            id: 'cm4',
            authorName: 'Carlos Mendes',
            body: 'Integração ERP está estável no ambiente de testes.',
            whenLabel: 'ontem',
          },
        ],
        history: [
          {
            id: 'h1',
            title: 'Enviado para validação',
            detail: 'Status alterado para Em validação',
            whenLabel: '27/10/2026',
          },
          {
            id: 'h2',
            title: 'Versão v1.0 publicada',
            detail: 'Ambiente configurado e compartilhado',
            whenLabel: '26/10/2026',
          },
        ],
      },
      {
        id: 'del-erp',
        title: 'Integração com ERP',
        version: 'v0.8',
        description: 'Sincronização de clientes e pedidos em desenvolvimento.',
        detailDescription:
          'Integração bidirecional com o ERP para clientes, pedidos e estoque, ainda em ciclo de desenvolvimento.',
        stageLabel: 'Integração',
        stageTone: 'red',
        ownerName: 'Carlos Mendes',
        ownerRole: 'Desenvolvedor',
        dueLabel: '05/11/2026',
        dueLongLabel: '05 de nov. de 2026',
        status: 'development',
        priorityLabel: 'Alta',
        progressPercent: 55,
        waitingClient: false,
        checklist: [
          { id: 'c1', label: 'API de clientes', done: true },
          { id: 'c2', label: 'API de pedidos', done: false },
          { id: 'c3', label: 'Testes de sincronização', done: false },
        ],
        files: [{ id: 'f1', name: 'Contrato_API_ERP.pdf', sizeLabel: '390 KB' }],
        comments: [
          {
            id: 'cm1',
            authorName: 'Carlos Mendes',
            body: 'Clientes sincronizando; pedidos na próxima sprint.',
            whenLabel: 'hoje',
          },
        ],
        history: [
          {
            id: 'h1',
            title: 'Entrada em desenvolvimento',
            detail: 'Trabalho iniciado na integração',
            whenLabel: '11/10/2026',
          },
        ],
      },
      {
        id: 'del-dashboard',
        title: 'Dashboard comercial',
        version: 'v0.1',
        description: 'Indicadores de funil e conversão previstos para novembro.',
        detailDescription:
          'Painel com funil de vendas, conversão por etapa e indicadores de acompanhamento comercial.',
        stageLabel: 'Desenvolvimento',
        stageTone: 'amber',
        ownerName: 'Rafael Oliveira',
        ownerRole: 'Desenvolvedor',
        dueLabel: '28/11/2026',
        dueLongLabel: '28 de nov. de 2026',
        status: 'planned',
        priorityLabel: 'Média',
        progressPercent: 10,
        waitingClient: false,
        checklist: [
          { id: 'c1', label: 'Definição de KPIs', done: true },
          { id: 'c2', label: 'Protótipo do painel', done: false },
        ],
        files: [],
        comments: [],
        history: [
          {
            id: 'h1',
            title: 'Entrega prevista criada',
            detail: 'Incluída no planejamento de novembro',
            whenLabel: '20/10/2026',
          },
        ],
      },
      {
        id: 'del-training',
        title: 'Capacitação da equipe',
        version: 'v1.0',
        description: 'Treinamento prático com o time comercial.',
        detailDescription: 'Sessões de capacitação presenciais e gravadas para o time comercial.',
        stageLabel: 'Capacitação',
        stageTone: 'green',
        ownerName: 'Ana Souza',
        ownerRole: 'Analista de implantação',
        dueLabel: '10/12/2026',
        dueLongLabel: '10 de dez. de 2026',
        status: 'planned',
        priorityLabel: 'Média',
        progressPercent: 0,
        waitingClient: false,
        checklist: [],
        files: [],
        comments: [],
        history: [],
      },
      {
        id: 'del-fields',
        title: 'Campos personalizados',
        version: 'v1.1',
        description: 'Campos do funil comercial configurados e aprovados.',
        detailDescription: 'Configuração dos campos personalizados do CRM conforme regras comerciais.',
        stageLabel: 'Configuração',
        stageTone: 'purple',
        ownerName: 'Ana Souza',
        ownerRole: 'Analista de implantação',
        dueLabel: '08/10/2026',
        dueLongLabel: '08 de out. de 2026',
        status: 'approved',
        priorityLabel: 'Média',
        progressPercent: 100,
        waitingClient: false,
        checklist: [
          { id: 'c1', label: 'Mapeamento de campos', done: true },
          { id: 'c2', label: 'Aceite do cliente', done: true },
        ],
        files: [{ id: 'f1', name: 'Configuracao_campos_CRM.pdf', sizeLabel: '260 KB' }],
        comments: [],
        history: [
          {
            id: 'h1',
            title: 'Entrega aprovada',
            detail: 'Aceite registrado',
            whenLabel: '08/10/2026',
          },
        ],
      },
      {
        id: 'del-pipeline',
        title: 'Pipeline de oportunidades',
        version: 'v1.0',
        description: 'Etapas do funil comercial parametrizadas.',
        detailDescription: 'Pipeline padrão de oportunidades com etapas e critérios de avanço.',
        stageLabel: 'Configuração',
        stageTone: 'purple',
        ownerName: 'Mariana Silva',
        ownerRole: 'Gerente do projeto',
        dueLabel: '12/10/2026',
        dueLongLabel: '12 de out. de 2026',
        status: 'approved',
        priorityLabel: 'Alta',
        progressPercent: 100,
        waitingClient: false,
        checklist: [],
        files: [],
        comments: [],
        history: [],
      },
      {
        id: 'del-uat',
        title: 'Homologação do sistema',
        version: 'v1.0',
        description: 'Ciclo formal de aceite das funcionalidades.',
        detailDescription: 'Homologação completa do CRM com roteiro de testes e aceite do cliente.',
        stageLabel: 'Homologação',
        stageTone: 'orange',
        ownerName: 'Mariana Silva',
        ownerRole: 'Gerente do projeto',
        dueLabel: '05/12/2026',
        dueLongLabel: '05 de dez. de 2026',
        status: 'planned',
        priorityLabel: 'Alta',
        progressPercent: 0,
        waitingClient: false,
        checklist: [],
        files: [],
        comments: [],
        history: [],
      },
      {
        id: 'del-docs',
        title: 'Manual do usuário',
        version: 'v0.9',
        description: 'Documentação operacional em validação interna.',
        detailDescription: 'Manual de uso do CRM para o time comercial e gestores.',
        stageLabel: 'Documentação',
        stageTone: 'sky',
        ownerName: 'Ana Souza',
        ownerRole: 'Analista de implantação',
        dueLabel: '20/11/2026',
        dueLongLabel: '20 de nov. de 2026',
        status: 'validation',
        priorityLabel: 'Baixa',
        progressPercent: 70,
        waitingClient: false,
        checklist: [
          { id: 'c1', label: 'Conteúdo revisado', done: true },
          { id: 'c2', label: 'Publicação no portal', done: false },
        ],
        files: [{ id: 'f1', name: 'Manual_usuario_CRM_rascunho.pdf', sizeLabel: '1.1 MB' }],
        comments: [
          {
            id: 'cm1',
            authorName: 'Ana Souza',
            body: 'Rascunho disponível para revisão.',
            whenLabel: 'há 1 dia',
          },
        ],
        history: [],
      },
      {
        id: 'del-golive',
        title: 'Go-live',
        version: 'v1.0',
        description: 'Entrada em produção do CRM.',
        detailDescription: 'Liberação do CRM em produção com suporte assistido na primeira semana.',
        stageLabel: 'Implantação',
        stageTone: 'slate',
        ownerName: 'Mariana Silva',
        ownerRole: 'Gerente do projeto',
        dueLabel: '15/12/2026',
        dueLongLabel: '15 de dez. de 2026',
        status: 'planned',
        priorityLabel: 'Alta',
        progressPercent: 0,
        waitingClient: false,
        checklist: [],
        files: [],
        comments: [],
        history: [],
      },
      {
        id: 'del-tests',
        title: 'Bateria de testes funcionais',
        version: 'v1.0',
        description: 'Execução dos casos de teste do CRM.',
        detailDescription: 'Suite de testes funcionais cobrindo fluxos comerciais principais.',
        stageLabel: 'Testes',
        stageTone: 'amber',
        ownerName: 'Carlos Mendes',
        ownerRole: 'Desenvolvedor',
        dueLabel: '18/11/2026',
        dueLongLabel: '18 de nov. de 2026',
        status: 'development',
        priorityLabel: 'Média',
        progressPercent: 35,
        waitingClient: false,
        checklist: [],
        files: [],
        comments: [],
        history: [],
      },
      {
        id: 'del-close',
        title: 'Encerramento do projeto',
        version: 'v1.0',
        description: 'Ata de encerramento e lições aprendidas.',
        detailDescription: 'Documentação de encerramento, indicadores finais e próximos passos.',
        stageLabel: 'Encerramento',
        stageTone: 'slate',
        ownerName: 'Mariana Silva',
        ownerRole: 'Gerente do projeto',
        dueLabel: '20/12/2026',
        dueLongLabel: '20 de dez. de 2026',
        status: 'planned',
        priorityLabel: 'Baixa',
        progressPercent: 0,
        waitingClient: false,
        checklist: [],
        files: [],
        comments: [],
        history: [],
      },
      {
        id: 'del-kickoff-docs',
        title: 'Ata de kick-off',
        version: 'v1.0',
        description: 'Registro da reunião inicial e combinados.',
        detailDescription: 'Ata aprovada com escopo, papéis e premissas do projeto.',
        stageLabel: 'Planejamento',
        stageTone: 'blue',
        ownerName: 'Mariana Silva',
        ownerRole: 'Gerente do projeto',
        dueLabel: '03/09/2026',
        dueLongLabel: '03 de set. de 2026',
        status: 'approved',
        priorityLabel: 'Média',
        progressPercent: 100,
        waitingClient: false,
        checklist: [],
        files: [{ id: 'f1', name: 'Ata_kickoff.pdf', sizeLabel: '140 KB' }],
        comments: [],
        history: [],
      },
      {
        id: 'del-solution',
        title: 'Definição da solução',
        version: 'v1.0',
        description: 'Arquitetura funcional aprovada.',
        detailDescription: 'Documento de solução com módulos, integrações e restrições.',
        stageLabel: 'Planejamento',
        stageTone: 'blue',
        ownerName: 'Rafael Oliveira',
        ownerRole: 'Consultor de negócios',
        dueLabel: '15/09/2026',
        dueLongLabel: '15 de set. de 2026',
        status: 'approved',
        priorityLabel: 'Alta',
        progressPercent: 100,
        waitingClient: false,
        checklist: [],
        files: [],
        comments: [],
        history: [],
      },
      {
        id: 'del-access',
        title: 'Acessos e perfis',
        version: 'v1.0',
        description: 'Perfis de usuário e matriz de acesso aprovados.',
        detailDescription: 'Definição de perfis, papéis e permissões iniciais do CRM.',
        stageLabel: 'Configuração',
        stageTone: 'purple',
        ownerName: 'Ana Souza',
        ownerRole: 'Analista de implantação',
        dueLabel: '20/09/2026',
        dueLongLabel: '20 de set. de 2026',
        status: 'approved',
        priorityLabel: 'Média',
        progressPercent: 100,
        waitingClient: false,
        checklist: [],
        files: [],
        comments: [],
        history: [],
      },
    ],
  },
};
